import React from 'react'
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  List,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useOutletContext, useSearchParams } from 'react-router-dom'
import ThreadListHeader from '../components/inbox/ThreadListHeader'
import ThreadRow from '../components/inbox/ThreadRow'
import Conversation from '../components/inbox/Conversation'
import LinkIdentityModal from '../components/LinkIdentityModal'
import MergeContactsModal from '../components/MergeContactsModal'
import BlockConfirmDialog from '../components/BlockConfirmDialog'
import type { Message } from '../store/messages'
import useMessagesStore from '../store/messages'
import useThreadsStore, { isDefaultFilters } from '../store/threads'
import useInboxStore from '../store/inbox'
import useUIStore from '../store/ui'
import type { ThreadSummary } from '../api/threads'
import useFilteredThreads from '../hooks/useFilteredThreads'
import {
  markRead,
  sendReplyByContact,
  sendReplyByMessage,
  type SendReplyResponse,
} from '../api/messages'
import {
  clearMessageFlagged,
  clearMessageOnAir,
  clearThreadFlags,
  clearThreadOnAir,
  markMessageFlagged,
  markMessageOnAir,
} from '../api/threads'
import { block, unblock } from '../api/block'
import type { MeResponse } from '../api/me'
import { usePageTitle } from '../components/common/PageTitleContext'
import ContactDrawer from '../components/contacts/ContactDrawer'
import useContactsDetailStore from '../store/contactsDetail'
import './Inbox.css'
import { latestFlaggedMessageId, latestOnAirMessageId } from '../utils/inbox/threadMarks'
import { warnInbox } from '../utils/inbox/safety'

type OutletContext = { me: MeResponse }

const Inbox: React.FC = () => {
  const { me } = useOutletContext<OutletContext>()
  const stationId = me.stations[0]?.id ?? null
  const isAdmin = me.user?.roles?.includes('admin') ?? false
  const [searchParams, setSearchParams] = useSearchParams()
  const drawerContactId = React.useMemo(() => {
    const value = searchParams.get('contactDrawer')
    if (!value) {
      return null
    }
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }, [searchParams])
  const contactDrawerOpen = drawerContactId !== null
  const contactButtonRef = React.useRef<HTMLButtonElement | null>(null)

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  usePageTitle('Inbox', { align: 'center', showBreadcrumbs: false, offsetAppBar: false })

  const threadFilters = useThreadsStore((state) => state.filters)
  const defaultPlatforms = useThreadsStore((state) => state.defaultPlatforms)
  const threads = useThreadsStore((state) => state.items)
  const filteredThreads = useFilteredThreads()
  const fetchingThreads = useThreadsStore((state) => state.fetching)
  const hydrateThreads = useThreadsStore((state) => state.hydrate)
  const setThreadUnreadCount = useThreadsStore((state) => state.setThreadUnreadCount)
  const recomputeThreadStatus = useThreadsStore((state) => state.recomputeThreadStatus)
  const applyThreadStatus = useThreadsStore((state) => state.applyThreadStatus)
  const upsertThreadFromMessage = useThreadsStore((state) => state.upsertFromMessage)
  const resetThreadFilters = useThreadsStore((state) => state.resetFilters)
  const filtersActive = !isDefaultFilters(threadFilters, defaultPlatforms)

  const updateMessage = useMessagesStore((state) => state.updateMessage)
  const loadThread = useMessagesStore((state) => state.loadThread)
  const setActiveContact = useMessagesStore((state) => state.setActiveContact)
  const markThreadReadUpTo = useMessagesStore((state) => state.markReadUpTo)
  const updateThreadMeta = useMessagesStore((state) => state.updateThreadMeta)
  const addOptimisticOutbound = useMessagesStore((state) => state.addOptimisticOutbound)
  const finalizeOptimisticOutbound = useMessagesStore((state) => state.finalizeOptimisticOutbound)
  const failOptimisticOutbound = useMessagesStore((state) => state.failOptimisticOutbound)
  const setJumpTarget = useMessagesStore((state) => state.setJumpTarget)

  const selectedThreadId = useInboxStore((state) => state.selectedThreadId)
  const lastSelectedThreadId = useInboxStore((state) => state.lastSelectedThreadId)
  const setSelectedThread = useInboxStore((state) => state.setSelectedThread)
  const clearSelectedThread = useInboxStore((state) => state.clearSelectedThread)
  const rememberSelectedThread = useInboxStore((state) => state.rememberSelectedThread)
  const clearRememberedThread = useInboxStore((state) => state.clearRememberedThread)

  const activeThreadState = useMessagesStore(
    React.useCallback(
      (state) => (state.activeContactId ? state.byContact[state.activeContactId] ?? null : null),
      [],
    ),
  )
  const activeContactId = useMessagesStore((state) => state.activeContactId)

  const conversationMessages = activeThreadState?.items ?? []
  const conversationMeta = activeThreadState?.meta ?? null
  const conversationLoading = activeThreadState?.loading ?? false
  const conversationError = activeThreadState?.error ?? null
  const firstUnreadId = activeThreadState?.firstUnreadId ?? null
  const conversationRequestId = activeThreadState?.requestId ?? null
  const activeThread = React.useMemo<ThreadSummary | null>(() => {
    if (!selectedThreadId) {
      return null
    }

    return threads.find((item) => item.contact_id === selectedThreadId) ?? null
  }, [threads, selectedThreadId])
  const [activeMessage, setActiveMessage] = React.useState<Message | null>(null)
  const [sendingReply, setSendingReply] = React.useState(false)
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(null)
  const [linkModalOpen, setLinkModalOpen] = React.useState(false)
  const [mergeModalOpen, setMergeModalOpen] = React.useState(false)
  const [blockDialogOpen, setBlockDialogOpen] = React.useState(false)
  const [accessError, setAccessError] = React.useState<string | null>(null)
  const [blockDefaultTarget, setBlockDefaultTarget] = React.useState<'identity' | 'contact'>('identity')
  const markOnOpen = useUIStore((state) => state.markOnOpen)
  const [pendingSelection, setPendingSelection] = React.useState<{ id: number; clearedFilters: boolean } | null>(null)
  const threadParam = React.useMemo(() => searchParams.get('thread'), [searchParams])
  const threadIdFromQuery = React.useMemo(() => {
    if (!threadParam) {
      return null
    }

    const parsed = Number(threadParam)
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null
    }

    return parsed
  }, [threadParam])

  const requestIdRef = React.useRef<string | undefined>(undefined)
  const lastMessageIdRef = React.useRef<number | null>(null)
  const lastIdentityEvent = useContactsDetailStore((state) => state.lastIdentityReassignedEvent)
  const setLastIdentityEvent = useContactsDetailStore((state) => state.setLastIdentityReassignedEvent)
  const previousStationRef = React.useRef<number | null>(null)
  const hasInitializedSelectionRef = React.useRef(false)
  const hasAppliedThreadQueryRef = React.useRef(false)

  const showSnackbar = React.useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ message, severity })
  }, [])

  const handleCloseSnackbar = () => setSnackbar(null)

  const openContactDrawer = React.useCallback(
    (contactId: number) => {
      if (!contactId) {
        return
      }
      const next = new URLSearchParams(searchParams)
      next.set('contactDrawer', String(contactId))
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const closeContactDrawer = React.useCallback(
    (options: { restoreFocus?: boolean } = {}) => {
      const next = new URLSearchParams(searchParams)
      next.delete('contactDrawer')
      setSearchParams(next, { replace: true })
      if (options.restoreFocus !== false) {
        window.setTimeout(() => {
          contactButtonRef.current?.focus()
        }, 0)
      }
    },
    [searchParams, setSearchParams],
  )

  const scrollThreadIntoView = React.useCallback((contactId: number) => {
    if (typeof document === 'undefined') {
      return
    }

    const element = document.querySelector<HTMLElement>(`[data-thread-id="${contactId}"]`)
    if (element) {
      element.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [])

  React.useEffect(() => {
    if (!contactDrawerOpen) {
      return
    }
    const nextContactId = selectedThreadId ?? activeThread?.contact_id ?? null
    if (nextContactId && nextContactId !== drawerContactId) {
      openContactDrawer(nextContactId)
    }
  }, [contactDrawerOpen, activeThread, drawerContactId, openContactDrawer, selectedThreadId])

  React.useEffect(() => {
    const previous = previousStationRef.current
    if (previous !== null && previous !== stationId) {
      clearSelectedThread()
      clearRememberedThread()
      setActiveMessage(null)
      setPendingSelection(null)
      hasInitializedSelectionRef.current = false
      hasAppliedThreadQueryRef.current = false
    }

    previousStationRef.current = stationId ?? null
  }, [stationId, clearRememberedThread, clearSelectedThread])

  React.useEffect(() => {
    if (!stationId) {
      return
    }

    if (threadParam && !threadIdFromQuery && !hasAppliedThreadQueryRef.current) {
      hasAppliedThreadQueryRef.current = true
      const next = new URLSearchParams(searchParams)
      next.delete('thread')
      setSearchParams(next, { replace: true })
      return
    }

    if (threadIdFromQuery && !hasAppliedThreadQueryRef.current) {
      hasAppliedThreadQueryRef.current = true
      if (selectedThreadId !== threadIdFromQuery) {
        setSelectedThread(threadIdFromQuery)
      } else {
        rememberSelectedThread(threadIdFromQuery)
      }
      setPendingSelection({ id: threadIdFromQuery, clearedFilters: false })

      const next = new URLSearchParams(searchParams)
      next.delete('thread')
      setSearchParams(next, { replace: true })
      return
    }

    if (!hasInitializedSelectionRef.current) {
      hasInitializedSelectionRef.current = true
      const restoreId = lastSelectedThreadId ?? selectedThreadId ?? null
      if (restoreId) {
        if (selectedThreadId !== restoreId) {
          setSelectedThread(restoreId)
        } else {
          rememberSelectedThread(restoreId)
        }
        setPendingSelection({ id: restoreId, clearedFilters: false })
      }
    }
  }, [
    stationId,
    threadParam,
    threadIdFromQuery,
    selectedThreadId,
    lastSelectedThreadId,
    rememberSelectedThread,
    searchParams,
    setSearchParams,
    setSelectedThread,
  ])

  React.useEffect(() => {
    if (!pendingSelection) {
      return
    }

    const targetId = pendingSelection.id
    if (!targetId) {
      setPendingSelection(null)
      return
    }

    const thread = threads.find((item) => item.contact_id === targetId)
    if (thread) {
      scrollThreadIntoView(targetId)
      setPendingSelection(null)
      return
    }

    if (fetchingThreads) {
      return
    }

    if (!pendingSelection.clearedFilters && filtersActive) {
      resetThreadFilters()
      setPendingSelection({ id: targetId, clearedFilters: true })
      return
    }

    if (selectedThreadId === targetId) {
      clearSelectedThread()
      setActiveMessage(null)
      setActiveContact(null, stationId)
    }
    clearRememberedThread()
    setPendingSelection(null)
  }, [
    pendingSelection,
    threads,
    fetchingThreads,
    filtersActive,
    resetThreadFilters,
    scrollThreadIntoView,
    selectedThreadId,
    clearSelectedThread,
    clearRememberedThread,
    setActiveContact,
    stationId,
  ])

  React.useEffect(() => {
    if (!selectedThreadId) {
      setPendingSelection(null)
    }
  }, [selectedThreadId])

  const loadThreads = React.useCallback(async () => {
    if (!stationId) {
      return
    }

    setAccessError(null)

    try {
      await hydrateThreads(stationId)
    } catch (error) {
      const apiError = error as Error & { status?: number; data?: unknown }
      const message = apiError instanceof Error ? apiError.message : 'Unable to load threads.'
      const data = apiError.data
      const code =
        data && typeof data === 'object' && 'code' in data
          ? String((data as { code?: unknown }).code ?? '')
          : ''

      if (apiError.status === 403 && (code === 'rpa_feature_locked' || code === 'rpa_feature_denied')) {
        setAccessError(message)
        return
      }

      showSnackbar(message, 'error')
    }
  }, [stationId, hydrateThreads, showSnackbar])

  React.useEffect(() => {
    if (!stationId) {
      return
    }
    void loadThreads()
  }, [stationId, threadFilters, loadThreads])

  React.useEffect(() => {
    if (!stationId) {
      setActiveMessage(null)
      setActiveContact(null, null)
      return
    }

    if (!selectedThreadId) {
      setActiveContact(null, stationId)
      return
    }

    if (activeContactId === selectedThreadId) {
      return
    }

    setActiveContact(selectedThreadId, stationId)
  }, [stationId, selectedThreadId, activeContactId, setActiveContact])

  React.useEffect(() => {
    if (!activeThreadState || !selectedThreadId) {
      return
    }

    if (activeThreadState.contactId !== selectedThreadId) {
      return
    }

    if (conversationRequestId && requestIdRef.current !== conversationRequestId) {
      requestIdRef.current = conversationRequestId

      if (!conversationMessages.length) {
        setActiveMessage(null)
        return
      }

      if (firstUnreadId) {
        const firstUnreadMessage = conversationMessages.find((item) => item.id === firstUnreadId) ?? null
        setActiveMessage(firstUnreadMessage ?? conversationMessages[conversationMessages.length - 1] ?? null)
      } else {
        setActiveMessage(conversationMessages[conversationMessages.length - 1] ?? null)
      }
    }
  }, [activeThreadState, selectedThreadId, conversationRequestId, conversationMessages, firstUnreadId])

  React.useEffect(() => {
    if (!activeThreadState || !selectedThreadId) {
      lastMessageIdRef.current = null
      return
    }

    if (activeThreadState.contactId !== selectedThreadId) {
      return
    }

    const latest = conversationMessages[conversationMessages.length - 1]?.id ?? null

    if (latest && latest !== lastMessageIdRef.current) {
      if (!activeMessage || activeMessage.id === lastMessageIdRef.current) {
        setActiveMessage(conversationMessages[conversationMessages.length - 1] ?? null)
      }
    }

    lastMessageIdRef.current = latest
  }, [conversationMessages, activeThreadState, selectedThreadId, activeMessage])

  React.useEffect(() => {
    if (!lastIdentityEvent) {
      return
    }
    if (contactDrawerOpen && drawerContactId === lastIdentityEvent.fromContactId) {
      openContactDrawer(lastIdentityEvent.toContactId)
      showSnackbar(`Moved to contact #${lastIdentityEvent.toContactId}`)
    }
    setLastIdentityEvent(null)
  }, [
    lastIdentityEvent,
    contactDrawerOpen,
    drawerContactId,
    openContactDrawer,
    showSnackbar,
    setLastIdentityEvent,
  ])

  React.useEffect(() => {
    if (!selectedThreadId || !activeThreadState || activeThreadState.contactId !== selectedThreadId) {
      return
    }

    const unreadCount = conversationMessages.reduce((total, item) => (item.is_read ? total : total + 1), 0)
    setThreadUnreadCount(selectedThreadId, unreadCount)
  }, [conversationMessages, activeThreadState, selectedThreadId, setThreadUnreadCount])

  const handleSelectThread = (thread: ThreadSummary) => {
    setSelectedThread(thread.contact_id)
    setActiveMessage(null)
    if (stationId) {
      setActiveContact(thread.contact_id, stationId)
    }
  }

  const handleThreadKeyDown = (thread: ThreadSummary) => (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key.toLowerCase() === 'enter') {
      event.preventDefault()
      handleSelectThread(thread)
    }
  }

  const jumpToThreadMessage = React.useCallback(
    (
      thread: ThreadSummary | null,
      messageId: number | null | undefined,
      options?: { highlight?: boolean },
    ) => {
      if (!thread) {
        warnInbox('[Inbox] Jump requested without thread', { messageId })
        return false
      }

      if (typeof messageId !== 'number' || !Number.isFinite(messageId) || messageId <= 0) {
        warnInbox('[Inbox] Jump requested without target', {
          threadId: thread.contact_id,
          messageId,
        })
        return false
      }

      if (!stationId) {
        warnInbox('[Inbox] Jump requested without active station', {
          threadId: thread.contact_id,
          messageId,
        })
      }

      if (selectedThreadId !== thread.contact_id) {
        handleSelectThread(thread)
      } else if (stationId) {
        setActiveContact(thread.contact_id, stationId)
      }

      scrollThreadIntoView(thread.contact_id)
      setJumpTarget(thread.contact_id, messageId, options)
      return true
    },
    [
      handleSelectThread,
      scrollThreadIntoView,
      selectedThreadId,
      setActiveContact,
      setJumpTarget,
      stationId,
    ],
  )

  const handleThreadJumpToOnAir = React.useCallback(
    (thread: ThreadSummary) => {
      const targetId = latestOnAirMessageId(thread)
      if (!targetId) {
        warnInbox('[Inbox] Jump to on-air requested without marker', {
          threadId: thread.contact_id,
        })
        return
      }
      jumpToThreadMessage(thread, targetId, { highlight: true })
    },
    [jumpToThreadMessage],
  )

  const handleThreadJumpToFlagged = React.useCallback(
    (thread: ThreadSummary) => {
      const targetId = latestFlaggedMessageId(thread)
      if (!targetId) {
        warnInbox('[Inbox] Jump to flagged requested without marker', {
          threadId: thread.contact_id,
        })
        return
      }
      jumpToThreadMessage(thread, targetId, { highlight: true })
    },
    [jumpToThreadMessage],
  )

  const handleConversationJumpToOnAir = React.useCallback(() => {
    if (!activeThread) {
      return
    }
    const targetId = latestOnAirMessageId(activeThread)
    if (!targetId) {
      warnInbox('[Inbox] Conversation jump to on-air requested without marker', {
        threadId: activeThread.contact_id,
      })
      return
    }
    jumpToThreadMessage(activeThread, targetId, { highlight: true })
  }, [activeThread, jumpToThreadMessage])

  const handleConversationJumpToFlagged = React.useCallback(() => {
    if (!activeThread) {
      return
    }
    const targetId = latestFlaggedMessageId(activeThread)
    if (!targetId) {
      warnInbox('[Inbox] Conversation jump to flagged requested without marker', {
        threadId: activeThread.contact_id,
      })
      return
    }
    jumpToThreadMessage(activeThread, targetId, { highlight: true })
  }, [activeThread, jumpToThreadMessage])

  const handleClearOnAir = React.useCallback(async () => {
    if (!stationId || !activeThread) {
      return
    }

    const contactId = activeThread.contact_id
    const previousStatus = {
      has_on_air: Boolean(activeThread.has_on_air ?? activeThread.hasOnAir),
      hasOnAir: Boolean(activeThread.has_on_air ?? activeThread.hasOnAir),
      latest_on_air_message_id:
        activeThread.latest_on_air_message_id ?? activeThread.latestOnAirMessageId ?? null,
      latestOnAirMessageId:
        activeThread.latest_on_air_message_id ?? activeThread.latestOnAirMessageId ?? null,
    }

    const threadState = useMessagesStore.getState().byContact[contactId]
    const optimisticIds = (threadState?.items ?? [])
      .filter((item) => item.is_on_air)
      .map((item) => item.id)
    const optimisticSet = new Set(optimisticIds)

    optimisticIds.forEach((id) => updateMessage(id, { is_on_air: false }))
    setActiveMessage((prev) =>
      prev && prev.contact_id === contactId && optimisticSet.has(prev.id)
        ? { ...prev, is_on_air: false }
        : prev,
    )
    applyThreadStatus(contactId, {
      has_on_air: false,
      hasOnAir: false,
      latest_on_air_message_id: null,
      latestOnAirMessageId: null,
    })

    try {
      const response = await clearThreadOnAir(stationId, contactId)
      const messageIds = response.message_ids ?? []
      messageIds.forEach((value) => {
        const id = Number(value)
        if (!Number.isNaN(id) && id > 0) {
          updateMessage(id, { is_on_air: false })
          if (optimisticSet.has(id)) {
            optimisticSet.delete(id)
          }
        }
      })

      setActiveMessage((prev) =>
        prev && prev.contact_id === contactId ? { ...prev, is_on_air: false } : prev,
      )
      applyThreadStatus(contactId, response.thread)
      recomputeThreadStatus(contactId)

      const clearedCount = response.cleared ?? messageIds.length
      const clearedLabel = clearedCount === 1 ? 'message' : 'messages'
      showSnackbar(`Cleared ${clearedCount} on-air ${clearedLabel}.`)
    } catch (error) {
      optimisticIds.forEach((id) => updateMessage(id, { is_on_air: true }))
      setActiveMessage((prev) =>
        prev && prev.contact_id === contactId && optimisticSet.has(prev.id)
          ? { ...prev, is_on_air: true }
          : prev,
      )
      applyThreadStatus(contactId, previousStatus)
      recomputeThreadStatus(contactId)
      const messageText = error instanceof Error ? error.message : 'Unable to clear on-air messages.'
      showSnackbar(messageText, 'error')
    }
  }, [
    activeThread,
    applyThreadStatus,
    recomputeThreadStatus,
    setActiveMessage,
    showSnackbar,
    stationId,
    updateMessage,
  ])

  const handleClearFlags = React.useCallback(async () => {
    if (!stationId || !activeThread) {
      return
    }

    const contactId = activeThread.contact_id
    const previousStatus = {
      has_flagged: Boolean(activeThread.has_flagged ?? activeThread.hasFlagged),
      hasFlagged: Boolean(activeThread.has_flagged ?? activeThread.hasFlagged),
      latest_flagged_message_id:
        activeThread.latest_flagged_message_id ?? activeThread.latestFlaggedMessageId ?? null,
      latestFlaggedMessageId:
        activeThread.latest_flagged_message_id ?? activeThread.latestFlaggedMessageId ?? null,
    }

    const threadState = useMessagesStore.getState().byContact[contactId]
    const optimisticIds = (threadState?.items ?? [])
      .filter((item) => item.is_flagged)
      .map((item) => item.id)
    const optimisticSet = new Set(optimisticIds)

    optimisticIds.forEach((id) => updateMessage(id, { is_flagged: false }))
    setActiveMessage((prev) =>
      prev && prev.contact_id === contactId && optimisticSet.has(prev.id)
        ? { ...prev, is_flagged: false }
        : prev,
    )
    applyThreadStatus(contactId, {
      has_flagged: false,
      hasFlagged: false,
      latest_flagged_message_id: null,
      latestFlaggedMessageId: null,
    })

    try {
      const response = await clearThreadFlags(stationId, contactId)
      const messageIds = response.message_ids ?? []
      messageIds.forEach((value) => {
        const id = Number(value)
        if (!Number.isNaN(id) && id > 0) {
          updateMessage(id, { is_flagged: false })
          if (optimisticSet.has(id)) {
            optimisticSet.delete(id)
          }
        }
      })

      setActiveMessage((prev) =>
        prev && prev.contact_id === contactId ? { ...prev, is_flagged: false } : prev,
      )
      applyThreadStatus(contactId, response.thread)
      recomputeThreadStatus(contactId)

      const clearedCount = response.cleared ?? messageIds.length
      const clearedLabel = clearedCount === 1 ? 'message' : 'messages'
      showSnackbar(`Cleared ${clearedCount} flagged ${clearedLabel}.`)
    } catch (error) {
      optimisticIds.forEach((id) => updateMessage(id, { is_flagged: true }))
      setActiveMessage((prev) =>
        prev && prev.contact_id === contactId && optimisticSet.has(prev.id)
          ? { ...prev, is_flagged: true }
          : prev,
      )
      applyThreadStatus(contactId, previousStatus)
      recomputeThreadStatus(contactId)
      const messageText = error instanceof Error ? error.message : 'Unable to clear flagged messages.'
      showSnackbar(messageText, 'error')
    }
  }, [
    activeThread,
    applyThreadStatus,
    recomputeThreadStatus,
    setActiveMessage,
    showSnackbar,
    stationId,
    updateMessage,
  ])

  const handleMarkReadToggle = async (message: Message, next: boolean) => {
    updateMessage(message.id, { is_read: next })
    setActiveMessage((prev) => (prev && prev.id === message.id ? { ...prev, is_read: next } : prev))

    const contactId = message.contact_id
    const unreadAfterUpdate = useMessagesStore
      .getState()
      .byContact[contactId]?.items.reduce((total, item) => (item.is_read ? total : total + 1), 0) ?? 0
    setThreadUnreadCount(contactId, unreadAfterUpdate)

    try {
      await markRead(message.id, stationId ?? 0, next)
    } catch (error) {
      updateMessage(message.id, { is_read: !next })
      setActiveMessage((prev) => (prev && prev.id === message.id ? { ...prev, is_read: !next } : prev))
      const revertedUnread = useMessagesStore
        .getState()
        .byContact[contactId]?.items.reduce((total, item) => (item.is_read ? total : total + 1), 0) ?? 0
      setThreadUnreadCount(contactId, revertedUnread)
      const messageText = error instanceof Error ? error.message : 'Unable to update read state.'
      showSnackbar(messageText, 'error')
    }
  }

  const handleFlagToggle = async (message: Message, next: boolean) => {
    if (!stationId) {
      showSnackbar('Station unavailable.', 'error')
      return
    }

    updateMessage(message.id, { is_flagged: next })
    setActiveMessage((prev) => (prev && prev.id === message.id ? { ...prev, is_flagged: next } : prev))
    recomputeThreadStatus(message.contact_id)

    try {
      const response = next
        ? await markMessageFlagged(stationId, message.contact_id, message.id)
        : await clearMessageFlagged(stationId, message.contact_id, message.id)

      updateMessage(response.message.id, {
        is_flagged: response.message.is_flagged,
        is_on_air: response.message.is_on_air,
      })
      setActiveMessage((prev) =>
        prev && prev.id === response.message.id
          ? {
              ...prev,
              is_flagged: response.message.is_flagged,
              is_on_air: response.message.is_on_air,
            }
          : prev,
      )
      applyThreadStatus(message.contact_id, response.thread)
    } catch (error) {
      updateMessage(message.id, { is_flagged: !next })
      setActiveMessage((prev) => (prev && prev.id === message.id ? { ...prev, is_flagged: !next } : prev))
      recomputeThreadStatus(message.contact_id)
      const messageText = error instanceof Error ? error.message : 'Unable to update flag state.'
      showSnackbar(messageText, 'error')
    }
  }

  const handleOnAirToggle = async (message: Message, next: boolean) => {
    if (!stationId) {
      showSnackbar('Station unavailable.', 'error')
      return
    }

    updateMessage(message.id, { is_on_air: next })
    setActiveMessage((prev) => (prev && prev.id === message.id ? { ...prev, is_on_air: next } : prev))
    recomputeThreadStatus(message.contact_id)

    try {
      const response = next
        ? await markMessageOnAir(stationId, message.contact_id, message.id)
        : await clearMessageOnAir(stationId, message.contact_id, message.id)

      updateMessage(response.message.id, {
        is_on_air: response.message.is_on_air,
        is_flagged: response.message.is_flagged,
      })
      setActiveMessage((prev) =>
        prev && prev.id === response.message.id
          ? {
              ...prev,
              is_on_air: response.message.is_on_air,
              is_flagged: response.message.is_flagged,
            }
          : prev,
      )
      applyThreadStatus(message.contact_id, response.thread)
    } catch (error) {
      updateMessage(message.id, { is_on_air: !next })
      setActiveMessage((prev) => (prev && prev.id === message.id ? { ...prev, is_on_air: !next } : prev))
      recomputeThreadStatus(message.contact_id)
      const messageText = error instanceof Error ? error.message : 'Unable to update on-air state.'
      showSnackbar(messageText, 'error')
    }
  }

  const handleAutoMarkedRead = React.useCallback(
    ({ upToId, contactId, unreadRemaining }: { upToId: number; contactId: number; newlyReadIds: number[]; unreadRemaining: number }) => {
      markThreadReadUpTo(contactId, upToId)
      setActiveMessage((prev) => (prev && prev.contact_id === contactId && prev.id <= upToId ? { ...prev, is_read: true } : prev))
      setThreadUnreadCount(contactId, unreadRemaining)
    },
    [markThreadReadUpTo, setThreadUnreadCount],
  )

  const handleOpenContactDetail = React.useCallback(() => {
    const contactId = selectedThreadId ?? conversationMeta?.contact.id ?? null
    if (contactId) {
      openContactDrawer(contactId)
    }
  }, [selectedThreadId, conversationMeta, openContactDrawer])

  const handleSendReply = async (
    text: string,
    options?: { source?: 'quick-reply' | 'composer' },
  ) => {
    if (!stationId || !selectedThreadId) {
      return null
    }

    const trimmed = text.trim()

    if (!trimmed) {
      return null
    }

    const source = options?.source ?? 'composer'

    const contactId = selectedThreadId
    const baseIdentityId = activeMessage?.contact_identity_id ?? conversationMeta?.active_identity ?? null
    const identityId = baseIdentityId && baseIdentityId > 0 ? baseIdentityId : null
    const identityFromMeta = identityId
      ? conversationMeta?.identities.find((identity) => identity.id === identityId) ?? null
      : null
    const identityPlatform =
      activeMessage?.platform ??
      identityFromMeta?.platform ??
      activeThread?.latest_platform ??
      (conversationMeta?.identities[0]?.platform ?? 'unknown')
    const contactName = conversationMeta?.contact.name ?? activeThread?.contact_name ?? ''
    const contactLocation = conversationMeta?.contact.location ?? null
    const replyToMessageId = activeMessage?.id ?? null

    const tempId = addOptimisticOutbound(contactId, trimmed, identityId ?? undefined, {
      stationId,
      replyToMessageId,
      sentByUserId: me.user.id,
      sentByDisplayName: me.user.name,
      platform: identityPlatform,
      contactName,
      contactLocation,
    })

    const optimisticThread = useMessagesStore.getState().byContact[contactId]
    const optimisticMessage = optimisticThread?.items.find((item) => item.id === tempId) ?? null

    updateThreadMeta(contactId, (prev) =>
      prev
        ? {
            ...prev,
            active_identity: identityId ?? prev.active_identity,
          }
        : prev,
    )

    const nowMs = Date.now()
    const optimisticIso = new Date(nowMs).toISOString()

    const optimisticForThreads: Message =
      optimisticMessage ?? {
        id: tempId,
        station_id: stationId,
        contact_id: contactId,
        contact_identity_id: identityId ?? activeMessage?.contact_identity_id ?? 0,
        platform: identityPlatform,
        content: trimmed,
        content_type: 'text',
        intent: null,
        received_at: optimisticIso,
        received_at_iso_utc: optimisticIso,
        received_at_epoch: Math.floor(nowMs / 1000),
        is_read: true,
        is_flagged: false,
        is_on_air: false,
        round_id: null,
        reply_text: null,
        reply_at: null,
        reply_at_iso_utc: null,
        reply_at_epoch: null,
        direction: 'out',
        reply_to_message_id: replyToMessageId,
        sent_by_user_id: me.user.id,
        transport_status: 'queued',
        transport_error: null,
        contact: {
          id: contactId,
          name: contactName,
          location: contactLocation,
        },
        identity: identityFromMeta ??
          activeMessage?.identity ?? {
            id: identityId ?? activeMessage?.contact_identity_id ?? 0,
            display_name: identityFromMeta?.display_name ?? contactName,
            platform: identityPlatform,
          },
        sent_by: {
          id: me.user.id,
          display_name: me.user.name,
        },
      }

    setActiveMessage(optimisticForThreads)

    const optimisticThreadId = (activeThread?.latest_message_id ?? 0) + 0.1

    upsertThreadFromMessage({
      ...optimisticForThreads,
      id: optimisticThreadId,
    })

    setSendingReply(true)

    const finalizePromise = (async () => {
      try {
        let response: SendReplyResponse

        if (activeMessage) {
          response = await sendReplyByMessage(activeMessage.id, stationId, trimmed, identityId ?? undefined)
        } else {
          response = await sendReplyByContact(contactId, stationId, trimmed, identityId ?? undefined)
        }

        const payloadMessage = response.message ?? null
        const resolvedMessage: Message = {
          ...optimisticForThreads,
          ...(payloadMessage ?? {}),
          id: response.id,
          transport_status: response.status,
          transport_error: response.error ?? payloadMessage?.transport_error ?? null,
          sent_by: payloadMessage?.sent_by ?? response.sent_by ?? optimisticForThreads.sent_by,
          sent_by_user_id:
            response.sent_by?.id ?? payloadMessage?.sent_by_user_id ?? optimisticForThreads.sent_by_user_id ?? null,
          reply_to_message_id: payloadMessage?.reply_to_message_id ?? optimisticForThreads.reply_to_message_id ?? null,
          content: payloadMessage?.content ?? optimisticForThreads.content,
          identity: payloadMessage?.identity ?? optimisticForThreads.identity,
          contact: payloadMessage?.contact ?? optimisticForThreads.contact,
        }

        const finalized = finalizeOptimisticOutbound(contactId, tempId, resolvedMessage)

        if (finalized) {
          upsertThreadFromMessage(finalized)
          setActiveMessage(finalized)
        }

        const isQuickReply = source === 'quick-reply'

        if (response.status === 'failed') {
          const fallback = isQuickReply ? 'Quick reply failed to send.' : 'Reply failed to send.'
          showSnackbar(response.error ?? fallback, 'error')
        } else if (response.status === 'queued') {
          showSnackbar(
            isQuickReply ? 'Quick reply queued for delivery.' : 'Reply queued for delivery.',
          )
        } else {
          showSnackbar(isQuickReply ? 'Quick reply sent' : 'Reply sent successfully.')
        }

        return finalized?.id ?? response.id ?? null
      } catch (error) {
        const fallbackError = source === 'quick-reply' ? 'Unable to send quick reply.' : 'Unable to send reply.'
        const messageText = error instanceof Error ? error.message : fallbackError
        failOptimisticOutbound(contactId, tempId, messageText)
        const failedForThreads = {
          ...optimisticForThreads,
          transport_status: 'failed' as const,
          transport_error: messageText,
        }
        upsertThreadFromMessage({ ...failedForThreads, id: optimisticThreadId })
        setActiveMessage((prev) =>
          prev && prev.id === tempId
            ? { ...prev, transport_status: 'failed', transport_error: messageText }
            : prev,
        )
        showSnackbar(messageText, 'error')
        throw error
      } finally {
        setSendingReply(false)
      }
    })()

    finalizePromise.catch(() => {
      // handled above; suppress unhandled rejection warnings
    })

    return { tempId, finalize: finalizePromise }
  }

  const handleBlockIdentity = async (reason: string) => {
    if (!stationId || !activeMessage) {
      return
    }

    await block(stationId, { identity_id: activeMessage.contact_identity_id, reason })
    updateThreadMeta(activeMessage.contact_id, (prev) =>
      prev
        ? {
            ...prev,
            blocked: { ...prev.blocked, identity: true },
          }
        : prev,
    )
    showSnackbar('Identity blocked.')
    void loadThreads()
  }

  const handleBlockContact = async (reason: string) => {
    if (!stationId || !activeMessage) {
      return
    }

    await block(stationId, { contact_id: activeMessage.contact_id, reason })
    updateThreadMeta(activeMessage.contact_id, (prev) =>
      prev
        ? {
            ...prev,
            blocked: { contact: true, identity: prev.blocked.identity },
          }
        : prev,
    )
    showSnackbar('Contact blocked.')
    void loadThreads()
  }

  const handleUnblock = async () => {
    if (!stationId || !activeMessage) {
      return
    }

    await unblock(stationId, {
      contact_id: activeMessage.contact_id,
      identity_id: activeMessage.contact_identity_id ?? undefined,
    })
    updateThreadMeta(activeMessage.contact_id, (prev) =>
      prev
        ? {
            ...prev,
            blocked: { contact: false, identity: false },
          }
        : prev,
    )
    showSnackbar('Block removed.')
    void loadThreads()
  }

  const allowIdentityBlock = Boolean(conversationMeta?.active_identity)
  const hasNoThreads = filteredThreads.length === 0

  const closeConversation = React.useCallback(() => {
    closeContactDrawer({ restoreFocus: false })
    clearSelectedThread()
    setActiveMessage(null)
    setActiveContact(null, stationId)
  }, [clearSelectedThread, closeContactDrawer, setActiveContact, stationId])

  return (
    <Box className="rpa-inbox-page">
      <div className="rpa-columns rpa-inbox-columns">
        <Paper variant="outlined" className="ThreadPane">
          <div className="ThreadPane__header">
            <ThreadListHeader />
          </div>
          <div className="ThreadPane__scroll">
            {!stationId ? (
              <Alert severity="info">You do not have access to any stations yet.</Alert>
            ) : fetchingThreads && threads.length === 0 ? (
              <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Loading conversations…
                </Typography>
              </Stack>
            ) : accessError ? (
              <Stack spacing={1.5} sx={{ p: 3 }}>
                <Alert severity="warning">{accessError}</Alert>
                <Typography variant="body2" color="text.secondary">
                  Contact your station admin if you believe this is a mistake.
                </Typography>
              </Stack>
            ) : hasNoThreads ? (
              <Stack spacing={1.5} sx={{ p: 3 }}>
                <Typography variant="body1" fontWeight={600}>
                  {filtersActive ? 'No messages match your filters' : 'No messages yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filtersActive
                    ? 'Adjust the filters or search term to broaden your results.'
                    : 'Messages will appear here once listeners start reaching out.'}
                </Typography>
              </Stack>
            ) : (
              <List disablePadding>
                {filteredThreads.map((thread) => (
                  <ThreadRow
                    key={thread.contact_id}
                    thread={thread}
                    selected={selectedThreadId === thread.contact_id}
                    onClick={() => handleSelectThread(thread)}
                    onKeyDown={handleThreadKeyDown(thread)}
                    onJumpToOnAir={handleThreadJumpToOnAir}
                    onJumpToFlagged={handleThreadJumpToFlagged}
                  />
                ))}
              </List>
            )}
          </div>
        </Paper>
        {isDesktop ? (
          <Conversation
            messages={conversationMessages}
            meta={conversationMeta}
            activeMessage={activeMessage}
            loading={conversationLoading}
            sending={sendingReply}
            markOnOpen={markOnOpen}
            disabled={!selectedThreadId}
            error={conversationError}
            firstUnreadId={firstUnreadId}
            requestId={conversationRequestId}
            onSendReply={handleSendReply}
            onMarkReadToggle={handleMarkReadToggle}
            onFlagToggle={handleFlagToggle}
            onOnAirToggle={handleOnAirToggle}
            onBlockIdentity={() => {
              setBlockDefaultTarget('identity')
              setBlockDialogOpen(true)
            }}
            onBlockContact={() => {
              setBlockDefaultTarget('contact')
              setBlockDialogOpen(true)
            }}
            onUnblock={handleUnblock}
            onLinkIdentity={() => setLinkModalOpen(true)}
            onMergeContact={() => setMergeModalOpen(true)}
            onAutoMarkedRead={handleAutoMarkedRead}
            onNotify={(message, severity = 'success') => showSnackbar(message, severity)}
            onOpenContactDetail={handleOpenContactDetail}
            contactDrawerOpen={contactDrawerOpen}
            contactButtonRef={contactButtonRef}
            onClearThreadOnAir={handleClearOnAir}
            onClearThreadFlags={handleClearFlags}
            onJumpToOnAir={handleConversationJumpToOnAir}
            onJumpToFlagged={handleConversationJumpToFlagged}
          />
        ) : null}
      </div>

      {!isDesktop && stationId ? (
        <Drawer
          anchor="right"
          open={Boolean(selectedThreadId)}
          onClose={closeConversation}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: 'min(100vw, 480px)',
              maxWidth: '100vw',
            },
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Conversation
              messages={conversationMessages}
              meta={conversationMeta}
              activeMessage={activeMessage}
              loading={conversationLoading}
              sending={sendingReply}
              markOnOpen={markOnOpen}
              disabled={!selectedThreadId}
              error={conversationError}
              firstUnreadId={firstUnreadId}
              requestId={conversationRequestId}
              onSendReply={handleSendReply}
              onMarkReadToggle={handleMarkReadToggle}
              onFlagToggle={handleFlagToggle}
              onOnAirToggle={handleOnAirToggle}
              onBlockIdentity={() => {
                setBlockDefaultTarget('identity')
                setBlockDialogOpen(true)
              }}
              onBlockContact={() => {
                setBlockDefaultTarget('contact')
                setBlockDialogOpen(true)
              }}
              onUnblock={handleUnblock}
              onLinkIdentity={() => setLinkModalOpen(true)}
              onMergeContact={() => setMergeModalOpen(true)}
              onAutoMarkedRead={handleAutoMarkedRead}
              onNotify={(message, severity = 'success') => showSnackbar(message, severity)}
              onOpenContactDetail={handleOpenContactDetail}
              contactDrawerOpen={contactDrawerOpen}
              contactButtonRef={contactButtonRef}
              onClose={closeConversation}
              onClearThreadOnAir={handleClearOnAir}
              onClearThreadFlags={handleClearFlags}
              onJumpToOnAir={handleConversationJumpToOnAir}
              onJumpToFlagged={handleConversationJumpToFlagged}
            />
          </Box>
        </Drawer>
      ) : null}

      {stationId ? (
        <ContactDrawer
          open={contactDrawerOpen}
          stationId={stationId}
          contactId={drawerContactId}
          isAdmin={isAdmin}
          onClose={() => closeContactDrawer()}
          onNavigateContact={openContactDrawer}
          onDeleted={() => {
            closeConversation()
            clearRememberedThread()
            void loadThreads()
          }}
        />
      ) : null}

      <LinkIdentityModal
        open={linkModalOpen}
        stationId={stationId ?? 0}
        identityId={activeMessage?.contact_identity_id ?? null}
        onClose={() => setLinkModalOpen(false)}
        onLinked={() => {
          setLinkModalOpen(false)
          if (stationId && selectedThreadId) {
            void loadThread(selectedThreadId, stationId, { force: true })
          }
        }}
      />

      <MergeContactsModal
        open={mergeModalOpen}
        stationId={stationId ?? 0}
        fromContactId={activeMessage?.contact_id ?? null}
        onClose={() => setMergeModalOpen(false)}
        onMerged={() => {
          setMergeModalOpen(false)
          showSnackbar('Contacts merged.')
          void loadThreads()
          clearSelectedThread()
          clearRememberedThread()
          setActiveMessage(null)
          setActiveContact(null, stationId)
        }}
      />

      <BlockConfirmDialog
        open={blockDialogOpen}
        contactName={conversationMeta?.contact.name}
        allowIdentity={allowIdentityBlock}
        loading={sendingReply}
        defaultTarget={blockDefaultTarget}
        onClose={() => setBlockDialogOpen(false)}
        onBlockIdentity={async (reason) => {
          await handleBlockIdentity(reason)
          setBlockDialogOpen(false)
        }}
        onBlockContact={async (reason) => {
          await handleBlockContact(reason)
          setBlockDialogOpen(false)
        }}
      />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar && (
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  )
}

export default Inbox
